import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useMediaQuery,
} from "@mui/material";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import { useTheme as useMuiTheme } from "@mui/material/styles";
import { useTheme } from "../contexts/ThemeContext";
import { useSystemButtonStyles } from "../hooks/useSystemButtonStyles";
import { submitBugReport } from "../utils/api/api";
import { resolveClubId } from "../utils/clubScope";
import {
  buildSupportErrorReport,
  extractClientErrorMessage,
} from "../utils/support/buildSupportErrorReport";
import { getMm } from "../theme/modernMaterialEnterprise";
import {
  SUPPORT_ERROR_OFFER_EVENT,
  offerSupportErrorReport,
} from "../utils/support/supportErrorReportEvents";
import { installClientSideSupportErrorReporting } from "../utils/support/installClientSideSupportErrorReporting";

export { offerSupportErrorReport };
export { offerSupportErrorReportForClientFailure } from "../utils/support/installClientSideSupportErrorReporting";

export default function SupportErrorOfferHost({ selectedPage, userInfo }) {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));
  const { dialogFormPrimarySx, dialogFormCancelSx } = useSystemButtonStyles();
  const clubID = resolveClubId(userInfo);

  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [successRef, setSuccessRef] = useState("");

  const lastOfferKeyRef = useRef("");
  const lastOfferTimeRef = useRef(0);

  useEffect(() => {
    installClientSideSupportErrorReporting();
  }, []);

  const reset = useCallback(() => {
    setPending(null);
    setSubmitting(false);
    setError("");
    setSuccessRef("");
  }, []);

  const handleClose = useCallback(() => {
    if (submitting) return;
    setOpen(false);
    reset();
  }, [submitting, reset]);

  useEffect(() => {
    const onOpen = (event) => {
      const detail = event?.detail;
      if (!detail || typeof detail !== "object") return;

      const page = detail.page || selectedPage || "";
      const action = detail.action || "";
      const userMessage = detail.userMessage || "";
      const dedupeKey = `${page}:${action}:${userMessage}:${extractClientErrorMessage(detail.error)}`;
      const now = Date.now();
      if (dedupeKey === lastOfferKeyRef.current && now - lastOfferTimeRef.current < 4000) {
        return;
      }
      lastOfferKeyRef.current = dedupeKey;
      lastOfferTimeRef.current = now;

      reset();
      setPending({
        page,
        action,
        userMessage,
        error: detail.error,
        context: detail.context || {},
        category: detail.category || "broken",
      });
      setOpen(true);
    };

    window.addEventListener(SUPPORT_ERROR_OFFER_EVENT, onOpen);
    return () => window.removeEventListener(SUPPORT_ERROR_OFFER_EVENT, onOpen);
  }, [reset, selectedPage]);

  const handleSend = async () => {
    if (!pending) return;
    setError("");
    setSubmitting(true);
    try {
      const payload = buildSupportErrorReport({
        page: pending.page,
        action: pending.action,
        userMessage: pending.userMessage,
        error: pending.error,
        context: pending.context,
        category: pending.category,
      });
      const result = await submitBugReport(payload, clubID);
      setSuccessRef(result.referenceId || "");
    } catch (err) {
      const msg =
        typeof err === "string"
          ? err
          : err?.response?.data?.error || err?.message || t("support_error_offer_send_failed");
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={isMobile}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : "8px",
          border: isMobile ? "none" : `1px solid ${getMm(isDarkMode).outlineVariant}`,
          bgcolor: getMm(isDarkMode).surfaceLowest,
          fontFamily: "'Plus Jakarta Sans', sans-serif",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          fontWeight: 700,
          fontSize: "1rem",
          color: getMm(isDarkMode).onSurface,
          pb: 1,
        }}
      >
        <SupportAgentOutlinedIcon sx={{ fontSize: 22, color: getMm(isDarkMode).primary }} />
        {t("support_error_offer_title")}
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        {successRef ? (
          <Box sx={{ py: 1 }}>
            <Alert severity="success" sx={{ borderRadius: "8px", mb: 1 }}>
              {t("support_error_offer_success")}
            </Alert>
            <Typography variant="body2" sx={{ color: getMm(isDarkMode).onSurfaceVariant }}>
              {t("support_error_offer_success_reference", { referenceId: successRef })}
            </Typography>
          </Box>
        ) : (
          <>
            <Typography
              variant="body2"
              sx={{ color: getMm(isDarkMode).onSurface, lineHeight: 1.5, mb: 1 }}
            >
              {t("support_error_offer_body")}
            </Typography>
            {pending?.userMessage ? (
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  color: getMm(isDarkMode).onSurfaceVariant,
                  fontStyle: "italic",
                }}
              >
                {pending.userMessage}
              </Typography>
            ) : null}
            {error ? (
              <Alert severity="error" sx={{ mt: 2, borderRadius: "8px" }}>
                {error}
              </Alert>
            ) : null}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2, pt: 0, gap: 1 }}>
        {successRef ? (
          <Button onClick={handleClose} sx={dialogFormPrimarySx} fullWidth={isMobile}>
            {t("close")}
          </Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={submitting} sx={dialogFormCancelSx}>
              {t("support_error_offer_cancel")}
            </Button>
            <Button
              onClick={handleSend}
              disabled={submitting}
              sx={dialogFormPrimarySx}
              startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
            >
              {submitting ? t("support_error_offer_sending") : t("support_error_offer_send")}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
